import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import Logo from "./Logo";
import { SERVICES, CONTACT } from "@/data/services";

export default function Footer() {
  return (
    <footer className="bg-euca-dark text-euca-mist" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo dark tagline />
            <p className="mt-6 text-sm leading-relaxed text-euca-mist/60">
              Trauma-informed therapy and assessment on the Gold Coast and in Canberra. With rooms in Ashmore and
              Deakin, plus in-home and telehealth services across the Gold Coast, Logan, Brisbane and surrounding
              areas.
            </p>
            <div className="mt-6 flex gap-3">
              {[
                { icon: Facebook, label: "Facebook", id: "social-facebook", url: "https://www.facebook.com/p/Eucalypt-Therapy-100094562946984/" },
                { icon: Instagram, label: "Instagram", id: "social-instagram", url: "https://www.instagram.com/eucalypttherapy/" },
              ].map(({ icon: Icon, label, id, url }) => (
                <a
                  key={id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={id}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-euca-mist/20 text-euca-mist/70 transition-colors duration-300 hover:border-euca-sage hover:text-euca-sage"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-euca-sage">Services</p>
            <ul className="mt-5 space-y-3 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-euca-mist/70 transition-colors hover:text-euca-mist" data-testid={`footer-service-${s.slug}`}>
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-euca-sage">Practice</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link to="/about" className="text-euca-mist/70 hover:text-euca-mist" data-testid="footer-about">About</Link></li>
              <li><Link to="/fees-funding" className="text-euca-mist/70 hover:text-euca-mist" data-testid="footer-fees">Fees &amp; Funding</Link></li>
              <li><Link to="/for-referrers" className="text-euca-mist/70 hover:text-euca-mist" data-testid="footer-referrers">Referrals</Link></li>
              <li><Link to="/contact" className="text-euca-mist/70 hover:text-euca-mist" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/privacy" className="text-euca-mist/70 hover:text-euca-mist" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/feedback" className="text-euca-mist/70 hover:text-euca-mist" data-testid="footer-feedback">Complaints &amp; Feedback</Link></li>
              <li><Link to="/crisis-support" className="text-euca-mist/70 hover:text-euca-mist" data-testid="footer-crisis">Emergency &amp; Crisis Support</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-euca-sage">Contact</p>
            <ul className="mt-5 space-y-4 text-sm text-euca-mist/70">
              <li data-testid="footer-address">
                <span className="mb-0.5 block text-sm font-semibold text-euca-mist">Gold Coast</span>
                {CONTACT.address}
              </li>
              <li data-testid="footer-address-canberra">
                <span className="mb-0.5 block text-sm font-semibold text-euca-mist">Canberra</span>
                {CONTACT.addressCanberra}
              </li>
              <li><a href={`mailto:${CONTACT.email}`} className="hover:text-euca-mist" data-testid="footer-email">{CONTACT.email}</a></li>
              <li><a href={CONTACT.phoneHref} className="hover:text-euca-mist" data-testid="footer-phone">{CONTACT.phone}</a></li>
              <li>By appointment, Monday to Friday</li>
              <li>Telehealth available across Gold Coast, Logan, Brisbane, Canberra and surrounds</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 rounded-2xl border border-euca-mist/15 bg-euca-mist/5 p-6 sm:p-8" data-testid="crisis-notice">
          <p className="text-sm font-semibold text-euca-mist">Eucalypt Therapy is not a crisis service.</p>
          <p className="mt-2 text-sm leading-relaxed text-euca-mist/60">
            If you or someone you know is in immediate danger, call <strong className="text-euca-mist">000</strong>.
            For 24/7 crisis support contact Lifeline on <strong className="text-euca-mist">13 11 14</strong>,
            Beyond Blue on <strong className="text-euca-mist">1300 22 4636</strong>, the Suicide Call Back Service
            on <strong className="text-euca-mist">1300 659 467</strong>, or Open Arms (veterans and families)
            on <strong className="text-euca-mist">1800 011 046</strong>.
          </p>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-euca-mist/10 pt-8 text-xs text-euca-mist/40">
          <p>© 2026 Eucalypt Therapy. All rights reserved. Ashmore, Gold Coast QLD.</p>
          <p>Accredited Mental Health Social Worker · Specialist Behaviour Support Practitioner · Registered NDIS Provider</p>
        </div>
      </div>
    </footer>
  );
}
